<template>
    <div class="page-container">
        <div class="settings-form">
            <!-- 顶部引导区域 -->
            <div class="guide-section">
                <div class="guide-content">
                    <h2 class="guide-title">
                        <el-icon class="guide-icon">
                            <User />
                        </el-icon>
                        用户管理
                    </h2>
                    <p class="guide-desc">管理商户的用户账号信息</p>
                </div>
                <div class="guide-actions">
                    <el-button type="primary" @click="fetchUserList" :loading="loading.refresh">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                        刷新列表
                    </el-button>
                </div>
            </div>

            <!-- 搜索区域 -->
            <div class="search-bar">
                <el-input v-model="searchForm.phone" placeholder="手机号" clearable size="large"
                    style="width: 180px; margin-right: 15px" />
                <el-input v-model="searchForm.nickname" placeholder="昵称" clearable size="large"
                    style="width: 180px; margin-right: 15px" />
                <el-select v-model="searchForm.status" placeholder="状态" clearable size="large"
                    style="width: 120px; margin-right: 15px">
                    <el-option label="全部" value="" />
                    <el-option label="正常" :value="1" />
                    <el-option label="禁用" :value="0" />
                </el-select>

                <el-button type="primary" size="large" @click="handleSearch">
                    <el-icon>
                        <Search />
                    </el-icon>
                    搜索
                </el-button>

                <el-button type="success" size="large" @click="openAddDialog" style="float: right">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    新增用户
                </el-button>
            </div>

            <!-- 用户列表 -->
            <el-table :data="userList" stripe style="width: 100%; margin-top: 20px" v-loading="loading.list">
                <el-table-column prop="phone" label="手机号" width="150" />
                <el-table-column prop="nickname" label="昵称" width="150" />
                <el-table-column label="头像" width="80">
                    <template #default="{ row }">
                        <el-avatar :size="40" :src="row.avatar" v-if="row.avatar">
                            <el-icon>
                                <User />
                            </el-icon>
                        </el-avatar>
                        <el-avatar :size="40" v-else>
                            <el-icon>
                                <User />
                            </el-icon>
                        </el-avatar>
                    </template>
                </el-table-column>
                <!-- 在 el-table 中添加新列 -->
                <el-table-column label="插件授权" width="120">
                    <template #default="{ row }">
                        <el-tag v-if="hasAuthorizedPlugins(row.application_market)" type="success">已授权</el-tag>
                        <el-tag v-else type="info">未授权</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="企鹅权限" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.zh_open_status === 2" type="success">可创建</el-tag>
                        <el-tag v-else type="warning">不可创建</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="快手权限" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.ks_open_status === 2" type="success">可创建</el-tag>
                        <el-tag v-else type="warning">不可创建</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="红薯权限" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.xhs_open_status === 2" type="success">可创建</el-tag>
                        <el-tag v-else type="warning">不可创建</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="抖音权限" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.dy_open_status === 2" type="success">可创建</el-tag>
                        <el-tag v-else type="warning">不可创建</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="百度权限" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.bd_open_status === 2" type="success">可创建</el-tag>
                        <el-tag v-else type="warning">不可创建</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="名片权限" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.mp_open_status === 2" type="success">可创建</el-tag>
                        <el-tag v-else type="warning">不可创建</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="性别" width="80">
                    <template #default="{ row }">
                        <el-tag v-if="row.gender === 1" type="primary">男</el-tag>
                        <el-tag v-else-if="row.gender === 2" type="danger">女</el-tag>
                        <el-tag v-else type="info">未知</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="密码状态" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.has_password" type="success">已设置</el-tag>
                        <el-tag v-else type="warning">未设置</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="状态" width="80">
                    <template #default="{ row }">
                        <el-tag v-if="row.status" type="success">正常</el-tag>
                        <el-tag v-else type="danger">禁用</el-tag>
                    </template>
                </el-table-column>

                <!-- 在 el-table-column 中添加过期时间列 -->
                <el-table-column prop="out_date" label="过期时间" width="180">
                    <template #default="{ row }">
                        <span v-if="row.out_date">{{ row.out_date }}</span>
                        <el-tag v-else type="info">永不过期</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="last_login_time" label="最后登录" width="180" />
                <el-table-column prop="last_login_ip" label="登录IP" width="120" />
                <el-table-column prop="create_time" label="创建时间" width="180" />
                <el-table-column prop="update_time" label="更新时间" width="180" />

                <el-table-column label="操作" width="320" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>
                            编辑
                        </el-button>
                        <el-button :type="scope.row.status ? 'warning' : 'success'" size="small"
                            @click="toggleStatus(scope.row)">
                            <el-icon>
                                <Switch />
                            </el-icon>
                            {{ scope.row.status ? '禁用' : '启用' }}
                        </el-button>
                        <el-button type="info" size="small" @click="openResetPasswordDialog(scope.row)">
                            <el-icon>
                                <Key />
                            </el-icon>
                            重置密码
                        </el-button>
                        <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
                            <el-icon>
                                <Delete />
                            </el-icon>
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchUserList"
                    @current-change="fetchUserList" />
            </div>
        </div>

        <!-- 新增/编辑用户对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="500px" :close-on-click-modal="false">
            <el-form ref="userForm" :model="userForm" :rules="formRules" label-width="80px">
                <el-form-item label="手机号" prop="phone" v-if="!isEditMode">
                    <el-input v-model="userForm.phone" placeholder="请输入手机号" />
                </el-form-item>

                <el-form-item label="密码" prop="password" v-if="!isEditMode">
                    <el-input v-model="userForm.password" placeholder="请输入密码" type="password" show-password />
                </el-form-item>

                <el-form-item label="昵称" prop="nickname">
                    <el-input v-model="userForm.nickname" placeholder="请输入昵称" />
                </el-form-item>

                <!-- 替换原来的头像输入框 -->
                <el-form-item label="头像">
                    <div class="avatar-upload">
                        <!-- 显示当前头像 -->
                        <div class="avatar-preview" v-if="userForm.avatar">
                            <el-image :src="userForm.avatar" class="avatar-image" :preview-src-list="[userForm.avatar]"
                                fit="cover" />
                            <div class="avatar-actions">
                                <el-button type="danger" size="small" @click="removeAvatar">
                                    <el-icon>
                                        <Delete />
                                    </el-icon>
                                    删除
                                </el-button>
                            </div>
                        </div>

                        <!-- 上传组件 -->
                        <el-upload class="avatar-uploader" :auto-upload="false" :show-file-list="false"
                            :on-change="handleAvatarChange" accept="image/*">
                            <el-button type="primary">
                                <el-icon>
                                    <Upload />
                                </el-icon>
                                {{ userForm.avatar ? '更换头像' : '上传头像' }}
                            </el-button>
                            <template #tip>
                                <div class="el-upload__tip">
                                    支持 jpg、png、gif 格式，大小不超过 20MB
                                </div>
                            </template>
                        </el-upload>
                    </div>
                </el-form-item>

                <el-form-item label="性别">
                    <el-radio-group v-model="userForm.gender">
                        <el-radio :label="0">未知</el-radio>
                        <el-radio :label="1">男</el-radio>
                        <el-radio :label="2">女</el-radio>
                    </el-radio-group>
                </el-form-item>

                <!-- 插件授权区域 -->
                <el-form-item label="插件授权" v-if="availablePlugins && Object.keys(availablePlugins).length > 0">
                    <div class="plugin-checkbox-group">
                        <div v-for="(enabled, plugin) in availablePlugins" :key="plugin" class="plugin-item">
                            <el-checkbox :model-value="userForm.application_market[plugin] || false"
                                @change="(value) => handlePluginChange(plugin, value)">
                                {{ getPluginName(plugin) }}
                            </el-checkbox>
                        </div>
                    </div>
                </el-form-item>

                <!-- 权限设置 -->
                <el-form-item label="企鹅权限">
                    <el-radio-group v-model="userForm.zh_open_status">
                        <el-radio :label="1">无法创建账号</el-radio>
                        <el-radio :label="2">可以创建账号</el-radio>
                    </el-radio-group>
                </el-form-item>

                <!-- 权限设置 -->
                <el-form-item label="抖音权限">
                    <el-radio-group v-model="userForm.dy_open_status">
                        <el-radio :label="1">无法创建账号</el-radio>
                        <el-radio :label="2">可以创建账号</el-radio>
                    </el-radio-group>
                </el-form-item>

                <!-- 权限设置 -->
                <el-form-item label="快手权限">
                    <el-radio-group v-model="userForm.ks_open_status">
                        <el-radio :label="1">无法创建账号</el-radio>
                        <el-radio :label="2">可以创建账号</el-radio>
                    </el-radio-group>
                </el-form-item>


                <!-- 权限设置 -->
                <el-form-item label="红薯权限">
                    <el-radio-group v-model="userForm.xhs_open_status">
                        <el-radio :label="1">无法创建账号</el-radio>
                        <el-radio :label="2">可以创建账号</el-radio>
                    </el-radio-group>
                </el-form-item>


                <!-- 权限设置 -->
                <el-form-item label="百度权限">
                    <el-radio-group v-model="userForm.bd_open_status">
                        <el-radio :label="1">无法创建账号</el-radio>
                        <el-radio :label="2">可以创建账号</el-radio>
                    </el-radio-group>
                </el-form-item>

                <el-form-item label="名片权限">
                    <el-radio-group v-model="userForm.mp_open_status">
                        <el-radio :label="1">无法创建名片</el-radio>
                        <el-radio :label="2">可以创建名片</el-radio>
                    </el-radio-group>
                </el-form-item>

                <el-form-item label="状态">
                    <el-switch v-model="userForm.status" :active-value="1" :inactive-value="0" />
                    <span class="switch-label">{{ userForm.status ? '正常' : '禁用' }}</span>
                </el-form-item>
            </el-form>

            <!-- 在 el-form 中添加过期时间字段 -->
            <el-form-item label="过期时间">
                <el-date-picker v-model="userForm.out_date" type="datetime" placeholder="选择过期时间"
                    value-format="YYYY-MM-DD HH:mm:ss" :disabled-date="disabledDate" style="width: 100%" />
                <div class="field-tip">留空表示永不过期</div>
            </el-form-item>
            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitUserForm">确认</el-button>
            </template>
        </el-dialog>

        <!-- 重置密码对话框 -->
        <el-dialog v-model="resetPasswordDialogVisible" title="重置密码" width="400px">
            <el-form ref="resetPasswordForm" :model="resetPasswordForm" :rules="resetPasswordRules" label-width="80px">
                <el-form-item label="新密码" prop="password">
                    <el-input v-model="resetPasswordForm.password" placeholder="请输入新密码" type="password" show-password />
                </el-form-item>
                <el-form-item label="确认密码" prop="confirmPassword">
                    <el-input v-model="resetPasswordForm.confirmPassword" placeholder="请确认新密码" type="password"
                        show-password />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="resetPasswordDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitResetPassword">确认重置</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';

import {
    Upload, User, Refresh, Search, Plus, Edit, Delete, Key, Switch
} from '@element-plus/icons-vue';

export default {
    name: 'UserManagement',
    components: { Upload, User, Refresh, Search, Plus, Edit, Delete, Key, Switch },
    data() {
        // 密码确认验证
        const validateConfirmPassword = (rule, value, callback) => {
            if (value !== this.resetPasswordForm.password) {
                callback(new Error('两次输入的密码不一致'));
            } else {
                callback();
            }
        };

        return {
            loading: {
                list: false,
                refresh: false
            },
            searchForm: {
                phone: '',
                nickname: '',
                status: ''
            },
            userList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            dialogVisible: false,
            resetPasswordDialogVisible: false,
            dialogTitle: '新增用户',
            isEditMode: false,
            availablePlugins: [], // 可用插件列表
            userForm: {
                id: null,
                phone: '',
                password: '',
                nickname: '',
                avatar: '',
                gender: 0,
                status: 1,
                out_date: '',
                application_market: {}, // 新增：插件授权
                zh_open_status: 1, // 新增：账号权限，默认1(无法创建账号)
                dy_open_status: 1, // 新增：账号权限，默认1(无法创建账号)
                ks_open_status: 1, // 新增：账号权限，默认1(无法创建账号)
                xhs_open_status: 1, // 新增：账号权限，默认1(无法创建账号)
                bd_open_status: 1, // 新增：账号权限，默认1(无法创建账号)
                mp_open_status: 1  // 新增：名片权限，默认1(无法创建名片)
            },
            resetPasswordForm: {
                id: null,
                password: '',
                confirmPassword: ''
            },
            formRules: {
                phone: [
                    { required: true, message: '请输入手机号', trigger: 'blur' },
                    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
                ],
                password: [
                    { required: true, message: '请输入密码', trigger: 'blur' },
                    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
                ],
                nickname: [
                    { required: true, message: '请输入昵称', trigger: 'blur' }
                ]
            },
            resetPasswordRules: {
                password: [
                    { required: true, message: '请输入新密码', trigger: 'blur' },
                    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
                ],
                confirmPassword: [
                    { required: true, message: '请确认新密码', trigger: 'blur' },
                    { validator: validateConfirmPassword, trigger: 'blur' }
                ]
            }
        };
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}');
        }
    },
    mounted() {
        this.fetchUserList();
        this.fetchAvailablePlugins();
    },
    methods: {
        // 获取用户列表
        async fetchUserList() {
            this.loading.list = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/user_management.php`,
                    {
                        action: 'list',
                        store_id: this.userInfo.id,
                        ...this.searchForm,
                        page: this.pagination.page,
                        page_size: this.pagination.pageSize
                    }
                );

                if (response.data.data) {
                    this.userList = response.data.data;
                    this.pagination.total = response.data.pagination.total;
                }
            } catch (error) {
                ElMessage.error('获取用户列表失败');
                console.error('获取用户列表错误:', error);
            } finally {
                this.loading.list = false;
            }
        },
        // 获取商户可用插件
        async fetchAvailablePlugins() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/user_management.php`,
                    {
                        action: 'get_plugins',
                        store_id: this.userInfo.id
                    }
                );

                if (response.data.plugins) {
                    this.availablePlugins = response.data.plugins;
                }
            } catch (error) {
                console.error('获取插件列表失败:', error);
            }
        },

        // 检查是否有授权的插件
        hasAuthorizedPlugins(applicationMarket) {
            if (!applicationMarket) return false;

            try {
                // 如果已经是对象，直接使用
                let plugins = applicationMarket;
                if (typeof applicationMarket === 'string') {
                    plugins = JSON.parse(applicationMarket);
                }

                // 检查是否有至少一个插件的值为 true
                return Object.values(plugins).some(value => value === true);
            } catch (e) {
                console.error('解析插件授权数据失败:', e);
                return false;
            }
        },

        // 获取插件显示名称
        getPluginName(plugin) {
            const pluginNames = {
                'smart_card_pyqgg': 'TX广告插件',
                'smart_card_bdgg': '百度广告插件',
                'smart_card_dygg': '抖音广告插件',
                'smart_card_ksgg': '快手广告插件',
                'smart_card_xhsgg': '小红书广告插件',
                'smart_card_sms': '短信广告插件',
                'smart_card_mail': '邮件广告插件',
                'smart_card_mbxx': '模版消息插件',
                // 后续可以继续添加其他插件
            };
            return pluginNames[plugin] || plugin;
        },

        // 处理头像选择变化
        async handleAvatarChange(file) {
            try {
                this.$message.info('头像上传中，请稍候...');
                const url = await this.uploadImage(file.raw);
                this.userForm.avatar = url;
                this.$message.success('头像上传成功');
            } catch (error) {
                this.$message.error(`头像上传失败: ${error.message}`);
                console.error('头像上传错误:', error);
            }
        },

        // 上传图片到OSS
        async uploadImage(file) {
            try {
                const loading = this.$loading({
                    lock: true,
                    text: '图片上传中...',
                    spinner: 'el-icon-loading',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                // 文件验证
                const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
                const maxSize = 20 * 1024 * 1024; // 2MB

                if (allowedTypes.indexOf(file.type) === -1) {
                    throw new Error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
                }

                if (file.size > maxSize) {
                    throw new Error(`文件大小超过限制 (20MB): ${(file.size / 1024 / 1024).toFixed(20)}MB`);
                }

                const signature = await getUploadSignature(file.type.split('/')[0]);
                const res = await uploadToOSS(file, signature);

                loading.close();
                return res.url;
            } catch (error) {
                this.$loading().close();
                console.error('图片上传失败:', error);
                throw error;
            }
        },

        // 删除头像
        removeAvatar() {
            this.userForm.avatar = '';
        },
        // 处理搜索
        handleSearch() {
            this.pagination.page = 1;
            this.fetchUserList();
        },
        // 禁用今天之前的日期
        disabledDate(time) {
            return time.getTime() < Date.now() - 24 * 60 * 60 * 1000;
        },
        // 打开新增对话框
        openAddDialog() {
            this.dialogTitle = '新增用户';
            this.isEditMode = false;
            this.userForm = {
                id: null,
                phone: '',
                password: '',
                nickname: '',
                avatar: '',
                gender: 0,
                status: 1,
                out_date: '',
                application_market: {},
                zh_open_status: 1,
                mp_open_status: 1,
                dy_open_status: 1,
                ks_open_status: 1,
                xhs_open_status: 1,
                bd_open_status: 1
            };
            this.fetchAvailablePlugins(); // 获取插件列表
            this.dialogVisible = true;
        },

        // 打开编辑对话框
        openEditDialog(row) {
            this.dialogTitle = '编辑用户';
            this.isEditMode = true;

            // 处理插件授权数据
            let applicationMarket = {};
            if (row.application_market) {
                try {
                    applicationMarket = JSON.parse(row.application_market);
                } catch (e) {
                    console.error('解析插件授权数据失败:', e);
                }
            }

            this.userForm = {
                id: row.id,
                phone: row.phone,
                password: '',
                nickname: row.nickname,
                avatar: row.avatar || '',
                gender: row.gender,
                status: row.status,
                out_date: row.out_date || '',
                application_market: applicationMarket,
                zh_open_status: row.zh_open_status || 1,
                mp_open_status: row.mp_open_status || 1,
                dy_open_status: row.dy_open_status || 1,
                ks_open_status: row.ks_open_status || 1,
                xhs_open_status: row.xhs_open_status || 1,
                bd_open_status: row.bd_open_status || 1
            };
            this.fetchAvailablePlugins(); // 获取插件列表
            this.dialogVisible = true;
        },

        // 插件选择变化处理 - Vue 3 写法
        handlePluginChange(plugin, value) {
            // Vue 3 中直接赋值即可，不需要 this.$set
            this.userForm.application_market[plugin] = value;

            // 如果需要确保响应式，可以创建一个新对象（通常不需要）
            // this.userForm.application_market = {
            //     ...this.userForm.application_market,
            //     [plugin]: value
            // };
        },

        // 提交用户表单
        submitUserForm() {
            this.$refs.userForm.validate(async (valid) => {
                if (!valid) {
                    ElMessage.warning('请完善表单信息');
                    return;
                }

                try {
                    const action = this.isEditMode ? 'update' : 'add';
                    const payload = {
                        ...this.userForm,
                        store_id: this.userInfo.id
                    };

                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/user_management.php`,
                        {
                            action,
                            ...payload
                        }
                    );

                    if (response.data.success) {
                        ElMessage.success(this.isEditMode ? '用户更新成功' : '用户添加成功');
                        this.dialogVisible = false;
                        this.fetchUserList();
                    } else {
                        ElMessage.error(response.data.error || '操作失败');
                    }
                } catch (error) {
                    ElMessage.error('操作失败');
                    console.error('操作错误:', error);
                }
            });
        },

        // 切换用户状态
        async toggleStatus(row) {
            const action = row.status ? '禁用' : '启用';

            try {
                await ElMessageBox.confirm(
                    `确定要${action}用户 ${row.phone} 吗？`,
                    `${action}用户`,
                    {
                        confirmButtonText: '确定',
                        cancelButtonText: '取消',
                        type: 'warning'
                    }
                );

                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/user_management.php`,
                    {
                        action: 'change_status',
                        store_id: this.userInfo.id,
                        id: row.id,
                        status: row.status ? 0 : 1
                    }
                );

                if (response.data.success) {
                    ElMessage.success(`${action}成功`);
                    this.fetchUserList();
                } else {
                    ElMessage.error(response.data.error || `${action}失败`);
                }
            } catch (error) {
                if (error !== 'cancel') {
                    ElMessage.error(`${action}失败`);
                }
            }
        },

        // 打开重置密码对话框
        openResetPasswordDialog(row) {
            this.resetPasswordForm = {
                id: row.id,
                password: '',
                confirmPassword: ''
            };
            this.resetPasswordDialogVisible = true;
        },

        // 提交重置密码
        submitResetPassword() {
            this.$refs.resetPasswordForm.validate(async (valid) => {
                if (!valid) {
                    ElMessage.warning('请完善密码信息');
                    return;
                }

                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/user_management.php`,
                        {
                            action: 'reset_password',
                            store_id: this.userInfo.id,
                            id: this.resetPasswordForm.id,
                            password: this.resetPasswordForm.password
                        }
                    );

                    if (response.data.success) {
                        ElMessage.success('密码重置成功');
                        this.resetPasswordDialogVisible = false;
                        this.fetchUserList();
                    } else {
                        ElMessage.error(response.data.error || '密码重置失败');
                    }
                } catch (error) {
                    ElMessage.error('密码重置失败');
                    console.error('重置密码错误:', error);
                }
            });
        },

        // 删除用户
        async handleDelete(id) {
            try {
                await ElMessageBox.confirm(
                    '确定要删除该用户吗？此操作不可恢复！',
                    '删除用户',
                    {
                        confirmButtonText: '确定删除',
                        cancelButtonText: '取消',
                        type: 'warning'
                    }
                );

                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/user_management.php`,
                    {
                        action: 'delete',
                        store_id: this.userInfo.id,
                        id: id
                    }
                );

                if (response.data.success) {
                    ElMessage.success('用户删除成功');
                    this.fetchUserList();
                } else {
                    ElMessage.error(response.data.error || '删除失败');
                }
            } catch (error) {
                if (error !== 'cancel') {
                    ElMessage.error('删除失败');
                }
            }
        }
    }
};
</script>

<style scoped lang="scss">
.page-container {
    padding: 20px;
    background: #f5f7fa;
    min-height: 100vh;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
}

/* 引导区域 */
.guide-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding: 24px;
    background: linear-gradient(135deg, #409eff 0%, #3375b9 100%);
    border-radius: 12px;
    color: white;
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}

.guide-content {
    .guide-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 8px;

        .guide-icon {
            font-size: 28px;
        }
    }

    .guide-desc {
        font-size: 14px;
        opacity: 0.9;
        margin: 0;
    }
}

/* 搜索区域 */
.search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

/* 分页容器 */
.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

/* 开关标签 */
.switch-label {
    margin-left: 10px;
    color: #606266;
    font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .guide-section {
        flex-direction: column;
        gap: 16px;
        text-align: center;
    }

    .search-bar {
        flex-direction: column;
        gap: 12px;

        .el-input,
        .el-select {
            width: 100% !important;
            margin-right: 0 !important;
        }

        .el-button {
            width: 100%;
            float: none !important;
        }
    }
}


// 头像上传样式
.avatar-upload {
    display: flex;
    align-items: flex-start;
    gap: 20px;
}

.avatar-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.avatar-image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    border: 2px solid #ebeef5;
    overflow: hidden;
}

.avatar-actions {
    display: flex;
    justify-content: center;
}

.avatar-uploader {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.el-upload__tip {
    font-size: 12px;
    color: #909399;
    margin-top: 5px;
}

// 响应式调整
@media (max-width: 768px) {
    .avatar-upload {
        flex-direction: column;
        align-items: center;
    }
}

.field-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

/* 插件授权样式 */
.plugin-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
}

.plugin-item {
    display: flex;
    align-items: center;
}

/* 响应式调整 */
@media (max-width: 768px) {
    .plugin-checkbox-group {
        flex-direction: column;
        gap: 10px;
    }
}
</style>